import PharmacistPortal from "@/pages/PharmacistPortal";
import AdminPharmacists from "@/pages/AdminPharmacists";
import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import BlendsafeToaster from "@/components/BlendsafeToaster";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AdminAuthProvider } from "@/lib/adminAuth";
import { usePageLoadRum } from "@/lib/rum";
import { captureAttribution } from "@/lib/attribution";
import RequireAdmin from "@/components/admin/RequireAdmin";
import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import BlendSafeClosingFooter from "@/components/BlendSafeClosingFooter";
import AuthModal from "@/components/AuthModal";
import PaywallModal from "@/components/PaywallModal";
import FoundingMomentDialog from "@/components/FoundingMomentDialog";
import Landing from "@/pages/Landing";
import Checker from "@/pages/Checker";
import Routine from "@/pages/Routine";
import Browse from "@/pages/Browse";
import BrowseMedication from "@/pages/BrowseMedication";
import BrowseIngredient from "@/pages/BrowseIngredient";
import BrowsePair from "@/pages/BrowsePair";
import SharedResult from "@/pages/SharedResult";
import WatchlistPage from "@/pages/Watchlist";
import Verify from "@/pages/Verify";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import ReceiptPage from "@/pages/ReceiptPage";
import BrandPreview from "@/pages/BrandPreview";
import Legal from "@/pages/Legal";
import AdminWaitlist from "@/pages/AdminWaitlist";
import WaitlistConfirm from "@/pages/WaitlistConfirm";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCatalog from "@/pages/AdminCatalog";
import AdminImport from "@/pages/AdminImport";
import AdminVerify from "@/pages/AdminVerify";
import AdminProducts from "@/pages/AdminProducts";
import AdminErrors from "@/pages/AdminErrors";
import AdminIngredientSubmissions from "@/pages/AdminIngredientSubmissions";
import AdminBarcodeSubmissions from "@/pages/AdminBarcodeSubmissions";
import AdminPhotoSubmissions from "@/pages/AdminPhotoSubmissions";
import AdminDataQuality from "@/pages/AdminDataQuality";
import AdminSeoSetup from "@/pages/AdminSeoSetup";
import AdminPairContent from "@/pages/AdminPairContent";
import AdminErrorLog from "@/pages/AdminErrorLog";
import AdminEvents from "@/pages/AdminEvents";
import IngredientInteractionPage from "@/pages/IngredientInteractionPage";
import MedicationInteractionsPage from "@/pages/MedicationInteractionsPage";
import IngredientPairPage from "@/pages/IngredientPairPage";
import ProductPage from "@/pages/ProductPage";
import MedicationPage from "@/pages/MedicationPage";
import MyRoutines from "@/pages/MyRoutines";
import Logos from "@/pages/Logos";
import FaqIndex from "@/pages/FaqIndex";
import FaqDetail from "@/pages/FaqDetail";
import About from "@/pages/About";
import Methodology from "@/pages/Methodology";
import Pricing from "@/pages/Pricing";
import Pro from "@/pages/Pro";
import CategoryIndex from "@/pages/CategoryIndex";
import CategoryDetail from "@/pages/CategoryDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/AdminBlog";
import AdminContentPlan from "@/pages/AdminContentPlan";
import AdminMaintenance from "@/pages/AdminMaintenance";
import AdminSweepReview from "@/pages/AdminSweepReview";
import AdminContacts from "@/pages/AdminContacts";
import AdminReportTemplates from "@/pages/AdminReportTemplates";
import AdminAlternatives from "@/pages/AdminAlternatives";
import AdminCourses from "@/pages/AdminCourses";
import AdminMedicationGuidance from "@/pages/AdminMedicationGuidance";

function Footer() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/verify")) return null;
  if (pathname.startsWith("/admin") || pathname.startsWith("/clinical")) return null;
  if (pathname === "/") return <BlendSafeClosingFooter />;
  return <SiteFooter />;
}

function HeaderBar({ onSignIn }) {
  const { pathname } = useLocation();
  // Hide the public site header on every admin route — admin shell has its own.
  if (pathname.startsWith("/admin") || pathname.startsWith("/clinical")) return null;
  return <Header onSignIn={onSignIn} />;
}

// Founding Member reveal — mounted once globally so high-intent triggers
// (waitlist signup, check result) can surface it from any page. Passive
// triggers (scroll / exit / fallback) are disabled on admin + verify routes.
function GlobalFoundingModal() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/clinical")) return null;
  const passive = !pathname.startsWith("/admin") && !pathname.startsWith("/verify");
  return <FoundingMomentDialog passiveTriggersEnabled={passive} />;
}

// Reset scroll to top on every route (pathname) change so a new page never
// inherits the previous page's scroll position. When the URL carries a hash
// (e.g. /#categories) we wait one tick for the new page to mount, then
// scroll to the matching anchor — this fixes "Categories shows Home" when
// navigating from another route, since otherwise the browser tries to jump
// before the anchor exists in the DOM.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer until the destination route's anchor element is in the DOM.
      const id = hash.replace(/^#/, "");
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 10) {
          // Anchor may render after the route mounts; retry up to 10×.
          setTimeout(() => tryScroll(attempt + 1), 60);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Fire-and-forget Real-User Monitoring: one `page_load` event per route.
function PageLoadTracker() {
  usePageLoadRum();
  // Capture UTMs / referrer / landing path on first paint so any waitlist
  // signup on the page carries the campaign attribution.
  useEffect(() => { try { captureAttribution(); } catch (_e) { /* no-op */ } }, []);
  return null;
}

function Shell() {
  const [authOpen, setAuthOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const { user } = useAuth();

  return (
    <>
      <ScrollToTop />
      <PageLoadTracker />
      <HeaderBar onSignIn={() => setAuthOpen(true)} />
      <Routes>
        <Route path="/clinical/*" element={<PharmacistPortal />} />
        <Route path="/admin/pharmacists" element={<RequireAdmin><AdminPharmacists /></RequireAdmin>} />
        <Route path="/" element={<Landing />} />
        <Route
          path="/check"
          element={
            <Checker
              onAuthNeeded={() => setAuthOpen(true)}
              onPaywall={() => setPaywallOpen(true)}
            />
          }
        />
        <Route
          path="/routine"
          element={
            <Routine
              onAuthNeeded={() => setAuthOpen(true)}
              onPaywall={() => setPaywallOpen(true)}
            />
          }
        />
        <Route path="/r/:id" element={<SharedResult />} />
        <Route path="/waitlist/confirm" element={<WaitlistConfirm />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/browse/med/:id" element={<BrowseMedication />} />
        <Route path="/browse/ing/:id" element={<BrowseIngredient />} />
        <Route path="/browse/pair/:medId/:ingId" element={<BrowsePair />} />

        {/* Public SEO interaction pages */}
        <Route
          path="/ingredients/:medication/:ingredient"
          element={<IngredientInteractionPage onPaywall={() => setPaywallOpen(true)} />}
        />
        <Route
          path="/interaction/:slug"
          element={<IngredientInteractionPage onPaywall={() => setPaywallOpen(true)} />}
        />
        <Route
          path="/ingredients/:combined"
          element={<IngredientPairPage onAuthNeeded={() => setAuthOpen(true)} />}
        />
        <Route
          path="/products/:slug"
          element={<ProductPage onAuthNeeded={() => setAuthOpen(true)} />}
        />
        <Route
          path="/medications/:slug"
          element={<MedicationPage />}
        />
        <Route
          path="/routines"
          element={<MyRoutines />}
        />
        <Route
          path="/watchlist"
          element={<WatchlistPage onAuthNeeded={() => setAuthOpen(true)} />}
        />
        <Route path="/verify" element={<Verify />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<Account />} />
        <Route path="/account/receipt/:sessionId" element={<ReceiptPage />} />
        <Route path="/brand" element={<BrandPreview />} />

        {/* Launch-required legal pages */}
        <Route path="/privacy" element={<Legal />} />
        <Route path="/terms" element={<Legal />} />
        <Route path="/disclaimer" element={<Legal />} />
        <Route path="/cookies" element={<Legal />} />

        {/* FAQ — SEO landing pages */}
        <Route path="/faq" element={<FaqIndex />} />
        <Route path="/faq/:slug" element={<FaqDetail />} />

        {/* Blog — auto-generated SEO articles */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Marketing + SEO landing pages */}
        <Route path="/about" element={<About />} />
        <Route path="/methodology" element={<Methodology />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pro" element={<Pro onAuthNeeded={() => setAuthOpen(true)} />} />
        <Route path="/categories" element={<CategoryIndex />} />
        <Route path="/categories/:slug" element={<CategoryDetail />} />

        <Route path="/admin/waitlist" element={<AdminWaitlist />} />
        <Route path="/admin/contacts" element={<AdminContacts />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard"
          element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/catalog"
          element={<RequireAdmin><AdminCatalog /></RequireAdmin>} />
        <Route path="/admin/import"
          element={<RequireAdmin><AdminImport /></RequireAdmin>} />
        <Route path="/admin/verify"
          element={<RequireAdmin><AdminVerify /></RequireAdmin>} />
        <Route path="/admin/products"
          element={<RequireAdmin><AdminProducts /></RequireAdmin>} />
        <Route path="/admin/errors"
          element={<RequireAdmin><AdminErrors /></RequireAdmin>} />
        <Route path="/admin/ingredient-submissions"
          element={<RequireAdmin><AdminIngredientSubmissions /></RequireAdmin>} />
        <Route path="/admin/barcode-submissions"
          element={<RequireAdmin><AdminBarcodeSubmissions /></RequireAdmin>} />
        <Route path="/admin/photo-submissions"
          element={<RequireAdmin><AdminPhotoSubmissions /></RequireAdmin>} />
        <Route path="/admin/data-quality"
          element={<RequireAdmin><AdminDataQuality /></RequireAdmin>} />
        <Route path="/admin/seo-setup"
          element={<RequireAdmin><AdminSeoSetup /></RequireAdmin>} />
        <Route path="/admin/pair-content"
          element={<RequireAdmin><AdminPairContent /></RequireAdmin>} />
        <Route path="/admin/content-plan"
          element={<RequireAdmin><AdminContentPlan /></RequireAdmin>} />
        <Route path="/admin/maintenance"
          element={<RequireAdmin><AdminMaintenance /></RequireAdmin>} />
        <Route path="/admin/sweep-review"
          element={<RequireAdmin><AdminSweepReview /></RequireAdmin>} />
        <Route path="/admin/error-log"
          element={<RequireAdmin><AdminErrorLog /></RequireAdmin>} />
        <Route path="/admin/events"
          element={<RequireAdmin><AdminEvents /></RequireAdmin>} />
        <Route path="/admin/blog"
          element={<RequireAdmin><AdminBlog /></RequireAdmin>} />
        <Route path="/admin/report-templates"
          element={<RequireAdmin><AdminReportTemplates /></RequireAdmin>} />
        <Route path="/admin/alternatives"
          element={<RequireAdmin><AdminAlternatives /></RequireAdmin>} />
        <Route path="/admin/courses"
          element={<RequireAdmin><AdminCourses /></RequireAdmin>} />
        <Route path="/admin/medication-guidance"
          element={<RequireAdmin><AdminMedicationGuidance /></RequireAdmin>} />

        <Route path="/logos" element={<Logos />} />
      </Routes>
      <Footer />
      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onAuthed={() => {
          if (user && !user.is_pro && user.checks_used >= user.quota) {
            setPaywallOpen(true);
          }
        }}
      />
      <PaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} />
      <GlobalFoundingModal />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AdminAuthProvider>
            <Shell />
            <BlendsafeToaster />
          </AdminAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
